import React, { useState, useEffect } from 'react';
import { Plus, Loader2, Settings, Trash2, Edit2, CheckCircle, AlertCircle, Users, Link as LinkIcon } from 'lucide-react';
import toast, { Toaster } from 'react-hot-toast';
import { API_URL } from '../../config';

export default function FacebookAppsManagement() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingApp, setEditingApp] = useState(null);
  const [formData, setFormData] = useState({
    app_name: '',
    app_id: '',
    app_secret: '',
    verify_token: '',
    webhook_suffix: '',
    max_clients: 5
  });

  useEffect(() => {
    loadApps();
  }, []);

  const loadApps = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/facebook-apps`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('📊 Apps chargées:', data.apps); // DEBUG
        setApps(data.apps || []);
      } else {
        toast.error('Erreur chargement apps');
      }
    } catch (error) {
      toast.error('Erreur réseau');
      console.error('Error:', error);
    }
    setLoading(false);
  };

  const openCreateModal = () => {
    setEditingApp(null);
    setFormData({
      app_name: '',
      app_id: '',
      app_secret: '',
      verify_token: '',
      webhook_suffix: '',
      max_clients: 5
    });
    setShowModal(true);
  };

  const openEditModal = (app) => {
    setEditingApp(app);
    setFormData({
      app_name: app.app_name,
      app_id: app.app_id,
      app_secret: app.app_secret,
      verify_token: app.verify_token,
      webhook_suffix: app.webhook_url.split('/').pop(),
      max_clients: app.max_clients
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const toastId = editingApp ? 'update' : 'create';
    toast.loading(editingApp ? 'Mise à jour...' : 'Création...', { id: toastId });
    
    try {
      const url = editingApp 
        ? `${API_URL}/api/admin/facebook-apps/${editingApp.$id}`
        : `${API_URL}/api/admin/facebook-apps`;
      
      const response = await fetch(url, {
        method: editingApp ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message || 'Succès !', { id: toastId });
        setShowModal(false);
        loadApps();
      } else {
        toast.error(data.message || 'Erreur', { id: toastId });
      }
    } catch (error) {
      toast.error('Erreur réseau', { id: toastId });
    }
  };

  const handleDelete = async (appId, appName) => {
    if (!window.confirm(`Supprimer l'app "${appName}" ? Cette action est irréversible.`)) {
      return;
    }
    
    toast.loading('Suppression...', { id: 'delete' });
    
    try {
      const response = await fetch(`${API_URL}/api/admin/facebook-apps/${appId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success('App supprimée', { id: 'delete' });
        loadApps();
      } else {
        toast.error(data.message || 'Erreur suppression', { id: 'delete' });
      }
    } catch (error) {
      toast.error('Erreur réseau', { id: 'delete' });
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copié !`);
  };

  return (
    <div className="space-y-6">
      <Toaster position="top-right" />
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-white mb-2">Apps Facebook</h2>
          <p className="text-blue-200">Gérer les applications Facebook pour contourner les limites API</p>
        </div>
        
        <button
          onClick={openCreateModal}
          className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 rounded-xl text-white transition-all hover:scale-105 shadow-lg"
        >
          <Plus className="w-5 h-5" />
          Nouvelle App
        </button>
      </div>

      {/* Stats globales */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-br from-blue-600 to-cyan-600 rounded-2xl p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <Settings className="w-10 h-10 opacity-80" />
            <div className="text-4xl font-bold">{apps.length}</div>
          </div>
          <h3 className="text-lg font-semibold">Apps Configurées</h3>
          <p className="text-white/80 text-sm">Total d'applications</p>
        </div>

        <div className="bg-gradient-to-br from-green-600 to-emerald-600 rounded-2xl p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <Users className="w-10 h-10 opacity-80" />
            <div className="text-4xl font-bold">
              {apps.reduce((sum, app) => sum + (app.current_clients || 0), 0)}
            </div>
          </div>
          <h3 className="text-lg font-semibold">Clients Actifs</h3>
          <p className="text-white/80 text-sm">Sur toutes les apps</p>
        </div>

        <div className="bg-gradient-to-br from-purple-600 to-pink-600 rounded-2xl p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <CheckCircle className="w-10 h-10 opacity-80" />
            <div className="text-4xl font-bold">
              {apps.reduce((sum, app) => sum + (app.available_slots || 0), 0)}
            </div>
          </div>
          <h3 className="text-lg font-semibold">Slots Disponibles</h3>
          <p className="text-white/80 text-sm">Capacité restante</p>
        </div>
      </div>

      {/* Liste des apps */}
      {loading ? (
        <div className="text-center py-12">
          <Loader2 className="w-12 h-12 text-blue-400 animate-spin mx-auto" />
        </div>
      ) : apps.length === 0 ? (
        <div className="text-center py-12 bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl">
          <Settings className="w-16 h-16 text-blue-400 mx-auto mb-4 opacity-50" />
          <h3 className="text-xl font-bold text-white mb-2">Aucune app configurée</h3>
          <p className="text-blue-200 mb-6">Créez votre première app Facebook pour commencer</p>
          <button
            onClick={openCreateModal}
            className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded-xl text-white transition-all"
          >
            <Plus className="w-5 h-5" />
            Créer une app
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6">
          {apps.map((app) => (
            <div
              key={app.$id}
              className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 hover:border-white/20 transition-all"
            >
              {/* Header de la carte */}
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-start gap-4">
                  <div className={`w-16 h-16 rounded-xl flex items-center justify-center ${
                    app.is_full 
                      ? 'bg-gradient-to-br from-red-500 to-orange-500' 
                      : 'bg-gradient-to-br from-blue-500 to-purple-500'
                  }`}>
                    <Settings className="w-8 h-8 text-white" />
                  </div>
                  
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-1">{app.app_name}</h3>
                    <div className="flex items-center gap-3">
                      <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm ${
                        app.is_full
                          ? 'bg-red-500/20 text-red-300 border border-red-500/30'
                          : 'bg-green-500/20 text-green-300 border border-green-500/30'
                      }`}>
                        <div className={`w-2 h-2 rounded-full ${
                          app.is_full ? 'bg-red-400' : 'bg-green-400 animate-pulse'
                        }`} />
                        {app.is_full ? 'Pleine' : 'Disponible'}
                      </span>
                      
                      <span className="text-blue-200 text-sm">
                        {app.current_clients || 0}/{app.max_clients} clients
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => openEditModal(app)}
                    className="p-2 bg-blue-500/20 hover:bg-blue-500/30 rounded-lg text-blue-300 transition-all"
                    title="Modifier"
                  >
                    <Edit2 className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(app.$id, app.app_name)}
                    disabled={(app.current_clients || 0) > 0}
                    className="p-2 bg-red-500/20 hover:bg-red-500/30 rounded-lg text-red-300 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    title={(app.current_clients || 0) > 0 ? 'Des clients utilisent cette app' : 'Supprimer'}
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Informations */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="bg-white/5 rounded-xl p-4">
                  <div className="text-blue-200 text-sm mb-1">App ID</div>
                  <div className="flex items-center gap-2">
                    <code className="text-white font-mono text-sm flex-1 truncate">
                      {app.app_id}
                    </code>
                    <button
                      onClick={() => copyToClipboard(app.app_id, 'App ID')}
                      className="p-1 hover:bg-white/10 rounded transition-all"
                    >
                      📋
                    </button>
                  </div>
                </div>

                <div className="bg-white/5 rounded-xl p-4">
                  <div className="text-blue-200 text-sm mb-1">Verify Token</div>
                  <div className="flex items-center gap-2">
                    <code className="text-white font-mono text-sm flex-1 truncate">
                      {app.verify_token}
                    </code>
                    <button
                      onClick={() => copyToClipboard(app.verify_token, 'Token')}
                      className="p-1 hover:bg-white/10 rounded transition-all"
                    >
                      📋
                    </button>
                  </div>
                </div>
              </div>

              {/* Webhook URL */}
              <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-xl p-4 mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <LinkIcon className="w-4 h-4 text-purple-300" />
                  <div className="text-purple-300 text-sm font-semibold">Webhook URL</div>
                </div>
                <div className="flex items-center gap-2">
                  <code className="text-white font-mono text-sm flex-1 break-all">
                    {app.webhook_url}
                  </code>
                  <button
                    onClick={() => copyToClipboard(app.webhook_url, 'Webhook URL')}
                    className="p-2 bg-purple-500/20 hover:bg-purple-500/30 rounded-lg transition-all flex-shrink-0"
                  >
                    📋
                  </button>
                </div>
              </div>

              {/* Clients utilisant cette app */}
              {app.client_names && app.client_names.length > 0 && (
                <div className="mb-6">
                  <div className="text-blue-200 text-sm font-semibold mb-3 flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Clients utilisant cette app ({app.client_names.length}) :
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {app.client_names.map((name, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-sm border border-blue-500/30"
                      >
                        {name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Progress bar */}
              <div>
                <div className="flex items-center justify-between text-sm text-blue-200 mb-2">
                  <span>Utilisation</span>
                  <span>{app.current_clients || 0}/{app.max_clients}</span>
                </div>
                <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all ${
                      app.is_full 
                        ? 'bg-gradient-to-r from-red-500 to-orange-500' 
                        : 'bg-gradient-to-r from-blue-500 to-purple-500'
                    }`}
                    style={{ width: `${((app.current_clients || 0) / app.max_clients) * 100}%` }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal Création/Édition - RESTE IDENTIQUE */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 border border-white/20 rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-2xl font-bold text-white mb-6">
              {editingApp ? 'Modifier l\'App' : 'Nouvelle App Facebook'}
            </h3>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-blue-200 text-sm font-medium mb-2">
                  Nom de l'app *
                </label>
                <input
                  type="text"
                  value={formData.app_name}
                  onChange={(e) => setFormData({...formData, app_name: e.target.value})}
                  className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ex: Agent IA App 1"
                  required
                />
              </div>

              <div>
                <label className="block text-blue-200 text-sm font-medium mb-2">
                  App ID Facebook *
                </label>
                <input
                  type="text"
                  value={formData.app_id}
                  onChange={(e) => setFormData({...formData, app_id: e.target.value})}
                  className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                  placeholder="123456789012345"
                  required
                />
              </div>

              <div>
                <label className="block text-blue-200 text-sm font-medium mb-2">
                  App Secret *
                </label>
                <input
                  type="password"
                  value={formData.app_secret}
                  onChange={(e) => setFormData({...formData, app_secret: e.target.value})}
                  className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                  placeholder="••••••••••••••••"
                  required
                />
              </div>

              <div>
                <label className="block text-blue-200 text-sm font-medium mb-2">
                  Verify Token *
                </label>
                <input
                  type="text"
                  value={formData.verify_token}
                  onChange={(e) => setFormData({...formData, verify_token: e.target.value})}
                  className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                  placeholder="mon_token_secure_123"
                  required
                />
                <p className="text-xs text-blue-300 mt-1">
                  Token personnalisé pour vérifier les webhooks
                </p>
              </div>

              <div>
                <label className="block text-blue-200 text-sm font-medium mb-2">
                  Suffixe Webhook *
                </label>
                <input
                  type="text"
                  value={formData.webhook_suffix}
                  onChange={(e) => setFormData({...formData, webhook_suffix: e.target.value})}
                  className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                  placeholder="app1"
                  required
                />
                <p className="text-xs text-blue-300 mt-1">
                  URL finale : https://fbapi.tdidigital.io/webhook/facebook/{formData.webhook_suffix || 'app1'}
                </p>
              </div>

              <div>
                <label className="block text-blue-200 text-sm font-medium mb-2">
                  Nombre maximum de clients
                </label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={formData.max_clients}
                  onChange={(e) => setFormData({...formData, max_clients: parseInt(e.target.value)})}
                  className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
                <p className="text-xs text-blue-300 mt-1">
                  Recommandé : 5 clients max par app (limite API 200 appels/heure)
                </p>
              </div>

              <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
                <h4 className="text-blue-300 font-semibold mb-2 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  Instructions
                </h4>
                <ol className="text-sm text-blue-200 space-y-1 list-decimal list-inside">
                  <li>Créez une app sur developers.facebook.com</li>
                  <li>Copiez l'App ID et l'App Secret</li>
                  <li>Créez un verify token personnalisé et sécurisé</li>
                  <li>Configurez le webhook dans Facebook avec l'URL générée</li>
                  <li>Activez les permissions : pages_manage_posts, pages_read_engagement</li>
                </ol>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-3 bg-white/10 hover:bg-white/20 rounded-xl text-white transition-all"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 rounded-xl text-white transition-all"
                >
                  {editingApp ? 'Mettre à jour' : 'Créer l\'app'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}